import { useEffect } from 'react'
import { CONTACT, SITE_URL } from '../data/site'
import { IMAGES } from '../data/solutions'

type SeoConfig = {
  title: string
  description: string
  path: string
  image?: string
  breadcrumbs?: { name: string; path: string }[]
  jsonLd?: Record<string, unknown>[]
}

function upsertTag(selector: string, create: () => HTMLElement): HTMLElement {
  let el = document.head.querySelector<HTMLElement>(selector)
  if (!el) {
    el = create()
    document.head.appendChild(el)
  }
  return el
}

function setMeta(attr: 'name' | 'property', key: string, content: string) {
  const el = upsertTag(`meta[${attr}="${key}"]`, () => {
    const m = document.createElement('meta')
    m.setAttribute(attr, key)
    return m
  })
  el.setAttribute('content', content)
}

export const organizationJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Rabemar Servicios Integrales',
  url: SITE_URL,
  logo: `${SITE_URL}/logo.png`,
  description:
    'Infraestructura operativa para empresas: logística, Facility Management, HSE, Control Tower, tecnología y sustentabilidad en un único socio operativo.',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Solís 1518',
    addressLocality: CONTACT.city,
    addressRegion: CONTACT.region,
    addressCountry: CONTACT.country,
  },
  contactPoint: [
    {
      '@type': 'ContactPoint',
      contactType: 'sales',
      telephone: '+54 2475 434452',
      email: CONTACT.email,
      areaServed: 'AR',
      availableLanguage: ['es'],
    },
  ],
}

export const localBusinessJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: 'Rabemar Servicios Integrales',
  image: IMAGES.hero,
  url: SITE_URL,
  telephone: '+54 2475 434452',
  email: CONTACT.email,
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Solís 1518',
    addressLocality: CONTACT.city,
    addressRegion: CONTACT.region,
    addressCountry: CONTACT.country,
  },
  areaServed: 'Argentina',
  priceRange: 'A convenir según alcance',
}

export function serviceJsonLd(name: string, description: string, path: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    description,
    serviceType: name,
    provider: { '@type': 'Organization', name: 'Rabemar Servicios Integrales', url: SITE_URL },
    areaServed: { '@type': 'Country', name: 'Argentina' },
    url: `${SITE_URL}${path}`,
  }
}

export function faqJsonLd(faqs: { question: string; answer: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  }
}

export function useSeo({ title, description, path, image, breadcrumbs, jsonLd = [] }: SeoConfig) {
  useEffect(() => {
    const url = `${SITE_URL}${path}`
    document.title = title

    setMeta('name', 'description', description)
    setMeta('name', 'robots', 'index, follow, max-image-preview:large')
    setMeta('property', 'og:type', 'website')
    setMeta('property', 'og:site_name', 'Rabemar Servicios Integrales')
    setMeta('property', 'og:locale', 'es_AR')
    setMeta('property', 'og:title', title)
    setMeta('property', 'og:description', description)
    setMeta('property', 'og:url', url)
    setMeta('property', 'og:image', image ?? IMAGES.hero)
    setMeta('name', 'twitter:card', 'summary_large_image')
    setMeta('name', 'twitter:title', title)
    setMeta('name', 'twitter:description', description)
    setMeta('name', 'twitter:image', image ?? IMAGES.hero)

    const canonical = upsertTag('link[rel="canonical"]', () => {
      const l = document.createElement('link')
      l.setAttribute('rel', 'canonical')
      return l
    })
    canonical.setAttribute('href', url)

    const blocks: Record<string, unknown>[] = [organizationJsonLd, ...jsonLd]
    if (breadcrumbs && breadcrumbs.length > 0) {
      blocks.push({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((b, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: b.name,
          item: `${SITE_URL}${b.path}`,
        })),
      })
    }

    document.head.querySelectorAll('script[data-rb-jsonld="true"]').forEach((n) => n.remove())
    blocks.forEach((block) => {
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.dataset.rbJsonld = 'true'
      script.textContent = JSON.stringify(block)
      document.head.appendChild(script)
    })
  }, [title, description, path, image, JSON.stringify(breadcrumbs), JSON.stringify(jsonLd)])
}
